import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Message, Modal, Radio } from '@arco-design/web-react';

import { AppPlazaRepoSelector } from '@/components-biz/AppPlazaRepoSelector';
import { type AppRepoImageQuery, AppRepoImageSelector } from '@/components-biz/AppRepoImageSelector';
import type { CommonImageItemType } from '@/components-biz/ImageSelect';
import { resolveImageSelectConfirmUrl } from '@/components-biz/ImageSelect/utils/resolveImageSelectUrl';

import styles from './app-image-source-modal.module.less';
import { AppImageSourceTab } from './appImageSourceTypes';

const MODAL_STYLE = { width: '60vw', minWidth: 700, maxWidth: 1000 };

export interface AppImageSourceModalProps {
  visible: boolean;
  sourceTab: AppImageSourceTab;
  modalSession: number;
  queryObj?: AppRepoImageQuery;
  initialSelectedKey?: string;
  /** 预置/已选镜像完整项；仅 key 高亮时确认会因无 item 报「镜像必选」，需一并回填 */
  initialSelectedItem?: CommonImageItemType | null;
  onSourceTabChange: (tab: AppImageSourceTab) => void;
  onCancel: () => void;
  onConfirm: (key: string, item: CommonImageItemType, imageUrl?: string) => void | Promise<void>;
}

function resolveItemKey(item?: CommonImageItemType | null): string {
  return String(item?.id ?? item?.key ?? '').trim();
}

/** 应用镜像来源选择弹窗：应用仓库 / 应用广场 Tab 切换 */
export function AppImageSourceModal({
  visible,
  sourceTab,
  modalSession,
  queryObj,
  initialSelectedKey,
  initialSelectedItem,
  onSourceTabChange,
  onCancel,
  onConfirm,
}: AppImageSourceModalProps) {
  const { t } = useTranslation();
  const [pendingKey, setPendingKey] = useState<string | undefined>();
  const [pendingItem, setPendingItem] = useState<CommonImageItemType | undefined>();

  const sourceOptions = useMemo(
    () => [
      { label: t('hooks:ai.app'), value: AppImageSourceTab.Repo },
      { label: t('home:market.app.center'), value: AppImageSourceTab.Plaza },
    ],
    [t],
  );

  const handleSelect = useCallback((item: CommonImageItemType) => {
    const key = String(item.id ?? item.key ?? '').trim();
    setPendingKey(key);
    setPendingItem(item);
  }, []);

  const handleOk = useCallback(async () => {
    const key = String(pendingKey || '').trim();
    const item = pendingItem || (key && resolveItemKey(initialSelectedItem) === key ? initialSelectedItem || undefined : undefined);
    if (!key || !item) {
      Message.error(t('common:validate.required.select', { name: t('common:image') }));
      return;
    }
    const imageUrl = await resolveImageSelectConfirmUrl('container', item);
    await onConfirm(key, item, imageUrl);
  }, [initialSelectedItem, onConfirm, pendingItem, pendingKey, t]);

  useEffect(() => {
    if (!visible) return;
    const key = String(initialSelectedKey || '').trim();
    setPendingKey(key || undefined);
    const itemKey = resolveItemKey(initialSelectedItem);
    setPendingItem(key && itemKey && key === itemKey ? initialSelectedItem || undefined : undefined);
  }, [initialSelectedItem, initialSelectedKey, modalSession, visible]);

  return (
    <Modal
      visible={visible}
      title={t('common:image.select')}
      onOk={handleOk}
      onCancel={onCancel}
      style={MODAL_STYLE}
      maskClosable={false}
      unmountOnExit
    >
      <div className={styles.modalForm}>
        <Form.Item label={t('data-center:image.source')} required className={styles.sourceTypeRow} layout="horizontal">
          <Radio.Group options={sourceOptions} type="button" size="small" value={sourceTab} onChange={onSourceTabChange} />
        </Form.Item>

        {sourceTab === AppImageSourceTab.Repo ? (
          <AppRepoImageSelector
            key={`repo-${modalSession}`}
            queryObj={queryObj}
            value={pendingKey ?? initialSelectedKey}
            onChange={handleSelect}
          />
        ) : (
          <AppPlazaRepoSelector key={`plaza-${modalSession}`} value={pendingKey ?? initialSelectedKey} onChange={handleSelect} />
        )}
      </div>
    </Modal>
  );
}
